import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { CloseOutlined } from "@/config/antd";
import WebIcon from "@/components/common/Web_Icons";

const AUTO_DISMISS_MS = 18000;
const TICK_MS = 100;

const FeedbackPrompt = ({ notification, onClose }) => {
  const [progressPercent, setProgressPercent] = useState(100);
  const [isHovered, setIsHovered] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const timerRef = useRef(null);
  const remainingMsRef = useRef(AUTO_DISMISS_MS);

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const triggerClose = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsExiting(true);
  }, []);

  useEffect(() => {
    if (!notification) return;
    remainingMsRef.current = AUTO_DISMISS_MS;
    setProgressPercent(100);
    setIsHovered(false);
    setIsExiting(false);
  }, [notification]);

  useEffect(() => {
    if (!notification || isExiting) return undefined;

    const stopTimer = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    if (isHovered) {
      stopTimer();
      return undefined;
    }

    timerRef.current = setInterval(() => {
      remainingMsRef.current -= TICK_MS;

      const pct = Math.max(
        (remainingMsRef.current / AUTO_DISMISS_MS) * 100,
        0
      );
      setProgressPercent(pct);

      if (remainingMsRef.current <= 0) {
        stopTimer();
        setIsExiting(true);
      }
    }, TICK_MS);

    return stopTimer;
  }, [notification, isHovered, isExiting]);

  const handleAnimationEnd = useCallback(
    (e) => {
      if (e.target !== e.currentTarget) return;
      if (isExiting) {
        onCloseRef.current?.();
      }
    },
    [isExiting]
  );

  if (!notification && !isExiting) return null;

  const { action, entityType, createdAt } = notification || {};

  const subtitle = createdAt
    ? new Date(createdAt).toLocaleString()
    : "Just now";

  const title = `New ${entityType || "notification"}`;
  const description = action
    ? `Action: ${action}`
    : "You have a new notification in your account.";

  return (
    <div
      className={`fixed top-4 right-4 z-50 w-full max-w-sm ${
        isExiting ? "toast-slide-out" : "toast-slide-in"
      }`}
      onAnimationEnd={handleAnimationEnd}>
      <div
        className="relative overflow-hidden rounded-xl border bg-white shadow-lg"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ borderColor: "var(--veegil-color-secondary)" }}>
        {/* Left accent strip */}
        <div
          className="absolute left-0 top-0 h-full w-1.5"
          style={{ backgroundColor: "var(--veegil-color-primary)" }}
        />

        {/* Card body */}
        <div className="flex items-start gap-3 p-4 pl-5">
          <div
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full"
            style={{ backgroundColor: "var(--veegil-color-secondary)" }}>
            <WebIcon icon="NotificationBing" className="w-10 h-10" />
          </div>

          <div className="min-w-0 flex-1">
            <p
              className="text-regular font-semibold"
              style={{ color: "var(--veegil-color-primary)" }}>
              {title}
            </p>
            <p className="mt-1 text-regular text-gray-700 break-words">
              {description}
            </p>
            <p className="mt-2 text-small text-gray-500">{subtitle}</p>
          </div>

          <button
            type="button"
            onClick={triggerClose}
            aria-label="Close notification"
            className="rounded-full p-1 transition-colors hover:bg-gray-100">
            <CloseOutlined style={{ color: "var(--veegil-color-primary)" }} />
          </button>
        </div>

        {/* Auto-dismiss progress bar at the bottom.
            Pauses visually (transition: none) while hovered. */}
        <div className="h-1 w-full bg-gray-100">
          <div
            className="h-full"
            style={{
              width: `${progressPercent}%`,
              backgroundColor: "var(--veegil-color-primary)",
              transition: isHovered ? "none" : `width ${TICK_MS}ms linear`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

FeedbackPrompt.propTypes = {
  notification: PropTypes.shape({
    _id: PropTypes.string,
    action: PropTypes.string,
    entityType: PropTypes.string,
    createdAt: PropTypes.string,
  }),
  onClose: PropTypes.func,
};

export default FeedbackPrompt;
